import { displayDigits } from '../../utils/displayDigits';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  GAUGE_START_DEG,
  GAUGE_TOTAL_DEG,
  angleDegToGaugeValue,
  buildGaugeTrackPath,
  gaugeArcLength,
  lerpGaugeColor,
  touchAngleDeg,
  valueToGaugeRatio,
} from '../../utils/gaugeGeometry';

export interface AtpCtlTargetGaugeProps {
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  size?: number;
  subtitle?: string;
}

/** Lightweight CTL arc dial — rAF-throttled drag, single progress stroke. */
export function AtpCtlTargetGauge({
  value,
  min,
  max,
  onChange,
  onDragStart,
  onDragEnd,
  size = 220,
  subtitle,
}: AtpCtlTargetGaugeProps) {
  const SZ = size;
  const CX = SZ / 2;
  const CY = SZ / 2;
  const R = SZ * 0.38;
  const SW = SZ * 0.09;
  const arcLen = gaugeArcLength(R);

  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);
  const ratioRef = useRef(valueToGaugeRatio(value, min, max));
  const rafRef = useRef(0);

  const [liveRatio, setLiveRatio] = useState(ratioRef.current);
  const [displayValue, setDisplayValue] = useState(value);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const r = valueToGaugeRatio(value, min, max);
    ratioRef.current = r;
    setLiveRatio(r);
    setDisplayValue(value);
  }, [value, min, max]);

  const trackPath = useMemo(() => buildGaugeTrackPath(CX, CY, R), [CX, CY, R]);

  const schedulePaint = useCallback(
    (ratio: number) => {
      ratioRef.current = ratio;
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        setLiveRatio(ratioRef.current);
        setDisplayValue(Math.round(min + ratioRef.current * (max - min)));
      });
    },
    [min, max],
  );

  const localFromEvent = useCallback((clientX: number, clientY: number) => {
    const el = svgRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const applyPointer = useCallback(
    (clientX: number, clientY: number) => {
      const pt = localFromEvent(clientX, clientY);
      if (!pt) return;
      const deg = touchAngleDeg(pt.x, pt.y, CX, CY);
      const next = angleDegToGaugeValue(deg, min, max);
      const ratio = valueToGaugeRatio(next, min, max);
      schedulePaint(ratio);
    },
    [CX, CY, localFromEvent, max, min, schedulePaint],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.currentTarget.setPointerCapture(e.pointerId);
      dragging.current = true;
      setIsDragging(true);
      onDragStart?.();
      applyPointer(e.clientX, e.clientY);
    },
    [applyPointer, onDragStart],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragging.current) return;
      applyPointer(e.clientX, e.clientY);
    },
    [applyPointer],
  );

  const endPointer = useCallback(() => {
    if (!dragging.current) return;
    dragging.current = false;
    setIsDragging(false);
    const v = Math.round(min + ratioRef.current * (max - min));
    setDisplayValue(v);
    setLiveRatio(ratioRef.current);
    onChange(v);
    onDragEnd?.();
  }, [max, min, onChange, onDragEnd]);

  const activeRatio = isDragging ? liveRatio : valueToGaugeRatio(value, min, max);
  const accent = lerpGaugeColor(activeRatio);
  const filled = Math.max(0.001, arcLen * activeRatio);
  const knobAngle = GAUGE_START_DEG + activeRatio * GAUGE_TOTAL_DEG;
  const knobRad = ((knobAngle - 90) * Math.PI) / 180;
  const knobX = CX + R * Math.cos(knobRad);
  const knobY = CY + R * Math.sin(knobRad);

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div
        style={{
          position: 'relative',
          width: SZ,
          height: SZ,
          touchAction: 'none',
          userSelect: 'none',
          cursor: isDragging ? 'grabbing' : 'grab',
          transform: isDragging ? 'scale(1.04)' : 'scale(1)',
          transition: isDragging ? 'none' : 'transform 0.15s ease',
        }}
      >
        <svg
          ref={svgRef}
          width={SZ}
          height={SZ}
          style={{ display: 'block', touchAction: 'none' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
          onLostPointerCapture={endPointer}
        >
          <path
            d={trackPath}
            stroke="transparent"
            strokeWidth={SW * 3.4}
            fill="none"
            strokeLinecap="round"
          />
          <path
            d={trackPath}
            stroke="#1A1E28"
            strokeWidth={SW}
            fill="none"
            strokeLinecap="round"
          />
          <path
            d={trackPath}
            stroke={accent}
            strokeWidth={SW}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={`${filled} ${arcLen}`}
          />
          <circle cx={knobX} cy={knobY} r={SW * 0.95} fill={accent} opacity={0.35} />
          <circle
            cx={knobX}
            cy={knobY}
            r={SW * 0.62}
            fill={accent}
            stroke="#F2F4F8"
            strokeWidth={2.5}
          />
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <span
            style={{
              fontSize: SZ * 0.22,
              fontWeight: 800,
              color: accent,
              lineHeight: 1.1,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {displayDigits(displayValue)}
          </span>
          {subtitle ? (
            <span
              style={{
                fontSize: SZ * 0.055,
                color: 'var(--fg-4, #8E94A2)',
                marginTop: 6,
                textAlign: 'center',
                paddingInline: 12,
              }}
            >
              {subtitle}
            </span>
          ) : null}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: SZ * 0.72,
          fontSize: 11,
          color: 'var(--fg-4, #8E94A2)',
          marginTop: 8,
        }}
      >
        <span>{displayDigits(min)}</span>
        <span>{displayDigits(max)}</span>
      </div>
    </div>
  );
}
